import { Box, SxProps } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CARD_PEEK_OFFSET, CAROUSEL_GAP } from '../../../constants/ui';
import { DomEventEnum } from '../../../types/shell';
import { CarouselProgress } from '../_components_';

interface IProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  getItemKey: (item: T) => string | number;
  sx?: SxProps;
  containerGutter?: number;
  footerItem?: React.ReactNode;
}

const scrollContainerStyles: SxProps = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  // Slides stretch to the tallest card so a placeholder slide sizing itself with height:100%
  // matches the real cards instead of collapsing to its own content.
  alignItems: 'stretch',
  overflowX: 'auto',
  gap: `${CAROUSEL_GAP}px`,
  flexWrap: 'nowrap',
  scrollSnapType: 'x mandatory',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '& > *': {
    flex: `0 0 calc(100% - ${CARD_PEEK_OFFSET}px)`,
    scrollSnapAlign: 'start',
    // A stretched flex item is only a definite height for its own children once it is itself a
    // column, which is what lets the slide's card fill the full slide.
    display: 'flex',
    flexDirection: 'column',
  },
};

export function CarouselView<T>({
  items,
  renderItem,
  getItemKey,
  sx = {},
  containerGutter = 16,
  footerItem,
}: IProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);

  const stepCount = useMemo(() => items.length + (footerItem ? 1 : 0), [items.length, footerItem]);

  const stepWidth = useCallback(() => {
    const container = scrollRef.current;
    if (!container?.firstElementChild) {
      return 0;
    }

    // A card with no measurable width (not laid out yet) must not produce a bare-gap step.
    const cardWidth = (container.firstElementChild as HTMLElement).getBoundingClientRect().width;

    return cardWidth ? cardWidth + CAROUSEL_GAP : 0;
  }, []);

  // Capture-phase listener so the active index also updates for scroll containers whose
  // scroll events don't bubble.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const updateActiveStep = () => {
      const width = stepWidth();
      if (!width) {
        return;
      }
      const index = Math.round(container.scrollLeft / width);
      setActiveStep((prev) => (prev === index ? prev : Math.max(0, Math.min(stepCount - 1, index))));
    };

    updateActiveStep();
    container.addEventListener(DomEventEnum.Scroll, updateActiveStep, { passive: true, capture: true });
    window.addEventListener(DomEventEnum.Resize, updateActiveStep);

    return () => {
      container.removeEventListener(DomEventEnum.Scroll, updateActiveStep, { capture: true });
      window.removeEventListener(DomEventEnum.Resize, updateActiveStep);
    };
  }, [stepCount, stepWidth]);

  const carouselSx = useMemo(
    () => ({
      ...scrollContainerStyles,
      // Full-bleed breakout: negative margin cancels the container padding, then padding +
      // scroll-padding restore the first card's rest position at the gutter.
      marginInline: `-${containerGutter}px`,
      paddingInline: `${containerGutter}px`,
      scrollPaddingLeft: `${containerGutter}px`,
      ...sx,
    }),
    [containerGutter, sx],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box ref={scrollRef} data-testid="carousel-scroller" sx={carouselSx as SxProps}>
        {items.map((item) => (
          <Box key={getItemKey(item)}>{renderItem(item)}</Box>
        ))}
        {footerItem && <Box>{footerItem}</Box>}
      </Box>
      <CarouselProgress steps={stepCount} activeStep={activeStep} />
    </Box>
  );
}
